import { Router } from '@angular/router';
import { AfterViewInit, Component, ElementRef, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AuthService } from '../../services/auth.service';
import { apiUrl } from '../../../environments/environment';

import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-verdentum-privacy',
  templateUrl: './verdentum-privacy.component.html',
  styleUrls: ['./verdentum-privacy.component.scss']
})
export class VerdentumPrivacyComponent implements OnInit, AfterViewInit {

  response:any;
  userDetails:any;
  apiLink = apiUrl+'/api/';

  /** Reading progress, 0-100, driven by the document pane's scroll position. */
  readProgress = 0;

  /**
   * The document's own sections, read off the rendered legal copy rather than
   * typed out here. The copy is a fixed block of ~1,100 lines that nobody is
   * going to keep a hand-written contents list in step with, so the list is
   * derived: whatever headings the text has are the headings the rail shows.
   */
  sections: { id: string; text: string; major: boolean }[] = [];
  activeSection = '';
  private headingEls: HTMLElement[] = [];

  /**
   * Does this org publish its own privacy policy? If it does, first run is three
   * steps rather than two, and the indicator has to say so from the first screen
   * — a "2 of 2" that turns into a third page is worse than no indicator at all.
   */
  get hasOrgStep(): boolean {
    const t = this.userDetails && this.userDetails.user && this.userDetails.user.org_privacy_policy_text;
    return !!(t && String(t).length);
  }

  /**
   * This page is ~1,100 lines of legal text. Without a progress line there is no
   * signal at all for how much is left, and the old layout also buried the accept
   * control at the very bottom. The bar is presentational only — nothing gates on
   * it, because forcing a scroll-to-end would just teach people to fling the
   * scrollbar rather than read.
   */
  onDocScroll(ev: any) {
    const el = ev && ev.target;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    this.readProgress = max > 0 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 100;

    // Which section is being read: the last heading whose top has passed the
    // reading line. Cheap enough to do on scroll for 15 headings; an
    // IntersectionObserver here would be more machinery than the job needs.
    const line = el.getBoundingClientRect().top + 120;
    let current = '';
    for (const h of this.headingEls) {
      if (h.getBoundingClientRect().top <= line) current = h.id; else break;
    }
    if (current && current !== this.activeSection) this.activeSection = current;
  }

  /** Builds the contents list once the legal copy is in the DOM. */
  ngAfterViewInit(): void {
    setTimeout(() => {
      const root: HTMLElement = this.host.nativeElement;
      const doc = root.querySelector('.vp-doc');
      if (!doc) return;
      const heads = Array.from(doc.querySelectorAll('h2, h4')) as HTMLElement[];
      this.headingEls = [];
      this.sections = heads.map((h, i) => {
        const text = (h.textContent || '').trim().replace(/\s+/g, ' ');
        if (!h.id) h.id = 'vp-sec-' + i;
        this.headingEls.push(h);
        // h4s in this document are sentence-length ("With whom do we share or
        // disclose your information? We don't sell your data.") — the rail is a
        // signpost, not a summary, so it takes the first clause.
        const short = text.length > 42 ? text.split(/[?.]/)[0].slice(0, 42).trim() + '…' : text;
        return { id: h.id, text: short, major: h.tagName.toLowerCase() === 'h2' };
      }).filter(s => s.text.length > 1);
      if (this.sections.length) this.activeSection = this.sections[0].id;
    }, 0);
  }

  /** Rail click -> scroll the document pane, not the window (the page is fixed). */
  jumpTo(id: string) {
    const root: HTMLElement = this.host.nativeElement;
    const pane = root.querySelector('.vp-scroll') as HTMLElement;
    const target = root.querySelector('#' + id) as HTMLElement;
    if (!pane || !target) return;
    pane.scrollTo({
      top: pane.scrollTop + target.getBoundingClientRect().top - pane.getBoundingClientRect().top - 16,
      behavior: 'smooth'
    });
    this.activeSection = id;
  }

  constructor(
    private host: ElementRef,
    private router: Router,
    private http: HttpClient,
    private toastr: ToastrService,
    private authService: AuthService
  ) {
    this.getUserDetails();
  }

  ngOnInit(): void {
  }

  async getUserDetails() {
    let userDetails = await this.authService.getUserDetails();
    if(userDetails) this.userDetails = userDetails;

    // Check if verdentum privacy policy is accepted
    if(this.userDetails.user.privacy_policy == 1) {
      await this.authService.setUserDetails({
        type: this.userDetails.type,
        token: this.userDetails.token,
        user: [this.userDetails.user]
      });
      this.router.navigate(['intro/org_privacy']);
    }
  }

  async updatePrivacy() {
    let userDetails = await this.authService.getUserDetails(),
    token = userDetails.token ? userDetails.token : '';
    
    // Create header and append JWT token
    let headers = new HttpHeaders({
      'Authorization': `Bearer: ${token}`
    });
    headers.append('Authorization', token);

    return this.http.post(this.apiLink+'usermgmt/update_vpp', {}, {headers: headers}).toPromise().then(response => {
      this.response = response;
      if(this.response.status == 0) {
        this.toastr.error(this.response.msg, 'Error!', {
          timeOut: 5000,
        });
        return false;
      }
      this.userDetails.user.privacy_policy = 1;
      this.authService.setUserDetails({
        type: this.userDetails.type,
        token: this.userDetails.token,
        user: [this.userDetails.user]
      });
      this.router.navigate(['intro/org_privacy']);
    });
  }

  async logout() {
    await this.authService.removeUserDetails();
    this.authService.trackAuthStatus();
  }

}
